// Full-screen story viewer with tap-to-advance, 10s auto-advance, tri-stage
// reveal (hz → +pinyin → +translation), react (warmth bump), and reply
// (opens chat with story as context). Closes on Escape.

import { useEffect, useState } from 'react';
import { X, Heart, MessageCircle, Plus, Check, Eye, Languages, EyeOff } from 'lucide-react';
import type { LibraryWord, Persona, Story, Word } from '@/lib/types';
import { StoryScene } from '@/components/scenes';
import { Portrait } from '@/components/portraits';
import { C } from '@/styles/tokens';

const AUTO_ADVANCE_MS = 10_000;

type RevealStage = 'hidden' | 'pinyin' | 'full';

interface StoryViewerProps {
  persona: Persona;
  stories: Story[];
  idx: number;
  library: LibraryWord[];
  onAdvance: (delta: number) => void;
  onClose: () => void;
  onReact: () => void;
  onChat: (story: Story) => void;
  onSaveWord: (word: Word) => void;
}

export function StoryViewer({
  persona,
  stories,
  idx,
  library,
  onAdvance,
  onClose,
  onReact,
  onChat,
  onSaveWord,
}: StoryViewerProps) {
  const story = stories[idx];
  const [stage, setStage] = useState<RevealStage>('hidden');

  // Reset reveal when the story object changes. Depending on `story` (not just
  // `idx`) catches persona-chain transitions where idx happens to stay the same
  // (e.g., last story of one persona to first story of the next, both at idx 0).
  useEffect(() => {
    setStage('hidden');
  }, [story]);

  // 10-second auto-advance, restarted on every story change.
  useEffect(() => {
    const t = window.setTimeout(() => onAdvance(1), AUTO_ADVANCE_MS);
    return () => window.clearTimeout(t);
  }, [idx, onAdvance]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onAdvance(-1);
      else if (e.key === 'ArrowRight') onAdvance(1);
      else if (e.key === ' ') {
        e.preventDefault();
        setStage((s) => (s === 'hidden' ? 'pinyin' : s === 'pinyin' ? 'full' : 'hidden'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onAdvance]);

  if (!story) return null;

  const showPinyin = stage !== 'hidden';
  const showEn = stage === 'full';
  const RevealIcon = stage === 'hidden' ? Eye : stage === 'pinyin' ? Languages : EyeOff;
  const revealLabel = stage === 'hidden' ? 'reveal pinyin' : stage === 'pinyin' ? 'translate' : 'hide';

  function isSaved(w: Word): boolean {
    return library.some((l) => l.hz === w.hz);
  }

  return (
    <div
      role="dialog"
      aria-modal
      aria-label={`Story from ${persona.en}`}
      className="fixed inset-0 z-40 flex items-stretch"
      style={{ background: 'rgba(26,22,20,0.92)' }}
    >
      <div
        className="relative flex-1 max-w-md mx-auto flex flex-col overflow-hidden"
        // Persona-tinted fallback fill. If the scene SVG doesn't extend the
        // full height (some AI-generated ones don't), this peeks through
        // underneath instead of the outer dialog's dark backdrop.
        style={{ background: `linear-gradient(180deg, ${persona.grad[0]}, ${persona.grad[1]})` }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <StoryScene scene={story.scene} svg={story.svg} persona={persona} />
        </div>
        <div className="absolute inset-x-0 top-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.45), transparent)' }} />

        <header className="relative z-20 px-4 pt-3 pb-2 safe-top">
          {/* Story progress segments — Instagram-style, one per story. Stays
              on its own row so the persona identity row below it can breathe. */}
          <div className="flex gap-1">
            {stories.map((_, i) => (
              <span
                key={i}
                className="flex-1 h-1 rounded-full overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.35)' }}
              >
                <span
                  className="block h-full"
                  style={{
                    width: i < idx ? '100%' : i === idx ? '0%' : '0%',
                    background: 'white',
                    animation: i === idx ? `storyFill ${AUTO_ADVANCE_MS}ms linear forwards` : 'none',
                  }}
                  key={`${i}-${idx}`}
                />
              </span>
            ))}
          </div>
          {/* Persona identity row — portrait + hanzi name + english name so the
              viewer can tell at a glance whose story they're in. The label
              group is pointer-events-none so taps in this strip still reach the
              prev/next zones underneath; only the close button stays live. */}
          <div className="mt-2.5 flex items-center gap-3 pointer-events-none">
            <span
              className="block w-11 h-11 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: '2px solid rgba(255,255,255,0.85)' }}
              aria-hidden
            >
              <Portrait persona={persona} />
            </span>
            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-baseline gap-2 leading-none">
                <span className="ff-zh-serif text-lg font-bold truncate">{persona.name}</span>
                <span className="ff-body text-[13px] opacity-80 truncate">{persona.en}</span>
              </div>
              <div className="ff-body text-[11px] opacity-70 mt-1 leading-none">{story.timeAgo}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0 pointer-events-auto"
              aria-label="Close stories"
              style={{ background: 'rgba(255,255,255,0.18)' }}
            >
              <X size={18} aria-hidden />
            </button>
          </div>
        </header>

        {/* Tap zones for prev/next. Sit above the StoryScene but below the bottom action panel. */}
        <button
          type="button"
          onClick={() => onAdvance(-1)}
          className="absolute left-0 top-12 bottom-72 w-[20%] z-10"
          aria-label="Previous story"
        />
        <button
          type="button"
          onClick={() => onAdvance(1)}
          className="absolute right-0 top-12 bottom-72 w-[80%] z-10"
          aria-label="Next story"
        />

        <div className="relative z-10 flex-1 pointer-events-none" />

        <div className="relative z-20 pb-5 safe-bottom text-white">
          {/* Translucent description card — sits inset from the screen edges
              with a smoked-glass background so the Chinese stays legible no
              matter what the scene behind it looks like. */}
          <div
            className="mx-4 rounded-2xl px-4 py-3"
            style={{
              background: 'rgba(26,22,20,0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <p className="ff-zh-serif text-2xl leading-snug flex-1">{story.hz}</p>
              <button
                type="button"
                onClick={() => setStage((s) => (s === 'hidden' ? 'pinyin' : s === 'pinyin' ? 'full' : 'hidden'))}
                className="ff-body text-[11px] font-semibold uppercase tracking-[0.18em] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-sm transition-all hover:opacity-100 active:scale-95 flex-shrink-0"
                style={{
                  background: 'rgba(255,255,255,0.92)',
                  color: C.ink,
                  opacity: 0.95,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                }}
                aria-label={
                  stage === 'hidden'
                    ? 'Reveal pinyin'
                    : stage === 'pinyin'
                      ? 'Show translation'
                      : 'Hide pinyin and translation'
                }
              >
                <RevealIcon size={13} aria-hidden />
                {revealLabel}
              </button>
            </div>
            {showPinyin && <p className="ff-body text-[12px] opacity-80 mb-1 anim-slide-up">{story.py}</p>}
            {showEn && <p className="ff-body text-[13px] italic opacity-90 anim-slide-up">{story.en}</p>}

            {showPinyin && story.words.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 mt-3 anim-slide-up" aria-label="Save these words">
                {story.words.map((w) => {
                  const saved = isSaved(w);
                  return (
                    <li key={w.hz}>
                      <button
                        type="button"
                        onClick={() => !saved && onSaveWord(w)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                        style={{
                          background: saved ? C.blush : 'rgba(255,255,255,0.22)',
                          color: saved ? C.vermillionDeep : 'white',
                        }}
                      >
                        {saved ? <Check size={11} aria-hidden /> : <Plus size={11} aria-hidden />}
                        <span className="ff-zh-serif">{w.hz}</span>
                        <span className="opacity-70">{w.py}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="mx-4 mt-3 flex gap-3">
            <button
              type="button"
              onClick={onReact}
              className="flex-1 py-3 rounded-full ff-body text-sm font-semibold inline-flex items-center justify-center gap-2 text-white"
              style={{ background: 'rgba(26,22,20,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            >
              <Heart size={16} aria-hidden /> React
            </button>
            <button
              type="button"
              onClick={() => onChat(story)}
              className="flex-1 py-3 rounded-full ff-body text-sm font-semibold inline-flex items-center justify-center gap-2 text-white"
              style={{ background: C.vermillion }}
            >
              <MessageCircle size={16} aria-hidden /> Reply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
